/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { SUBSCRIPTION_OPTIONS, SubscriptionTier, stripeService } from 'services/StripeService';
import './auth.css';

// CSS styles for modal
const modalOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
};

const modalStyle: React.CSSProperties = {
  backgroundColor: 'white',
  padding: '5px 20px 5px',
  borderRadius: '10px',
  width: '400px',
  maxHeight: '90%',
  overflowY: 'auto',
  position: 'relative',
  textShadow: 'none',
  color: 'black',
  fontFamily: 'arial',
  textAlign: 'left',
  fontSize: 'small'
};

const closeButtonStyle: React.CSSProperties = {
  fontSize: 'x-small',
  height: '16px',
  borderRadius: '5px',
  background: 'lightgrey',
  padding: '2px',
  width: '16px',
  marginRight: '-14px',
  cursor: 'pointer'
};

const buttonStyle: React.CSSProperties = {
  fontSize: 'small',
  height: '24px',
  width: '180px',
  cursor: 'pointer'
};

const subscriptionCardStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  borderRadius: '8px',
  padding: '15px',
  margin: '10px 0',
  cursor: 'pointer',
  transition: 'all 0.3s ease'
};

const selectedCardStyle: React.CSSProperties = {
  ...subscriptionCardStyle,
  border: '2px solid #4285f4',
  backgroundColor: '#f0f7ff'
};

interface SubscriptionModalProps {
  user: User;
  currentTier?: string | null;
  subscriptionExpirationDate?: Date | null;
  onClose: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ user, currentTier, subscriptionExpirationDate, onClose }) => {
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const isUpgrading = currentTier === SubscriptionTier.SMITH || currentTier === SubscriptionTier.MASTERSMITH;

  // Calculate the exact discount amount for LIFETIME upgrades
  const calculateLifetimeDiscount = (): number => {
    if (!subscriptionExpirationDate || !currentTier) return 0;

    const now = new Date();
    const msRemaining = subscriptionExpirationDate.getTime() - now.getTime();
    const daysRemaining = Math.max(0, Math.floor(msRemaining / (1000 * 60 * 60 * 24)));
    const monthsRemaining = daysRemaining / 30.44; // Average days per month

    let discountPerMonth = 0;
    if (currentTier === SubscriptionTier.SMITH) {
      discountPerMonth = 0.75;
    } else if (currentTier === SubscriptionTier.MASTERSMITH) {
      discountPerMonth = 1.50;
    }

    return Math.round(monthsRemaining * discountPerMonth * 100) / 100; // Round to cents
  };

  const lifetimeDiscount = calculateLifetimeDiscount();

  // Helper function to determine if a tier is lower than or equal to current tier
  const isTierLowerOrEqual = (tier: SubscriptionTier, currentTier: string | null | undefined): boolean => {
    if (!currentTier) return false;

    const tierHierarchy: Record<string, number> = {
      [SubscriptionTier.SMITH]: 1,
      [SubscriptionTier.MASTERSMITH]: 2,
      [SubscriptionTier.LIFETIME]: 3
    };

    const tierLevel = tierHierarchy[tier] || 0;
    const currentLevel = tierHierarchy[currentTier] || 0;

    return tierLevel <= currentLevel;
  };

  // Auto-select the next tier up when upgrading
  useEffect(() => {
    if (isUpgrading && !selectedTier) {
      if (currentTier === SubscriptionTier.SMITH) {
        setSelectedTier(SubscriptionTier.MASTERSMITH);
      } else if (currentTier === SubscriptionTier.MASTERSMITH) {
        setSelectedTier(SubscriptionTier.LIFETIME);
      }
    }
  }, [isUpgrading, currentTier, selectedTier]);

  const handleSubscribe = async () => {
    if (!selectedTier) {
      setError('Please select a subscription plan');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // If upgrading to LIFETIME, always use checkout (it's a one-time payment, not a subscription upgrade)
      if (selectedTier === SubscriptionTier.LIFETIME || !isUpgrading) {
        // Create a new checkout session for LIFETIME or new subscriptions
        console.log('Creating checkout session for tier:', selectedTier);
        const sessionId = await stripeService.createCheckoutSession(user, selectedTier);

        if (!sessionId) {
          throw new Error('Failed to create checkout session');
        }

        console.log('Redirecting to checkout with sessionId:', sessionId);
        await stripeService.redirectToCheckout(sessionId);
      } else if (isUpgrading) {
        // Use the upgrade API for subscription-to-subscription upgrades
        console.log('Upgrading subscription to:', selectedTier);
        const result = await stripeService.upgradeSubscription(user, selectedTier);

        if (result.success) {
          setSuccess('Successfully upgraded! Your storage limit has been increased to 500 monsters.');
          // Close the modal after a short delay to show the success message
          setTimeout(() => {
            onClose();
          }, 2000);
        } else {
          throw new Error(result.error || 'Failed to upgrade subscription');
        }
      }
    } catch (error: any) {
      console.error('Error during subscription process:', error);
      // Show a more specific error message if available
      const errorMessage = error?.message || 'An error occurred during the subscription process. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={modalOverlayStyle}>
      <div style={modalStyle}>
        <div style={{ textAlign: 'right' }}>
          <button style={closeButtonStyle} onClick={onClose}>X</button>
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2>{isUpgrading ? 'Upgrade Your Subscription' : 'Choose Your Subscription'}</h2>
          <p>{isUpgrading ? `You're currently on: ${currentTier}` : 'Select a plan that works for you'}</p>

          <table>
            <tbody>
              <tr>
                {SUBSCRIPTION_OPTIONS.map((option) => {
                  const isCurrentTier = option.id === currentTier;
                  const isDisabled = isUpgrading && isTierLowerOrEqual(option.id, currentTier);

                  return (
                    <td key={option.id}>
                      <div
                        style={{
                          ...(selectedTier === option.id ? selectedCardStyle : subscriptionCardStyle),
                          opacity: isDisabled ? 0.6 : 1,
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          position: 'relative'
                        }}
                        onClick={() => !isDisabled && setSelectedTier(option.id)}
                      >
                        {isCurrentTier && (
                          <div style={{
                            position: 'absolute',
                            top: '5px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#4285f4',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: 'x-small',
                            fontWeight: 'bold'
                          }}>
                            CURRENT
                          </div>
                        )}
                        <h3>{option.name}</h3>
                        <h2>{option.price}</h2>
                        <p>{option.description}</p>
                      </div>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>

          {isUpgrading && selectedTier && selectedTier !== SubscriptionTier.LIFETIME && (
            <p style={{ fontSize: 'small', color: '#666', marginTop: '10px' }}>
              You'll be charged the prorated difference based on your remaining subscription time.
              {selectedTier === SubscriptionTier.MASTERSMITH && ' Your renewal will be updated to $19.99/year.'}
              {selectedTier === SubscriptionTier.SMITH && ' Your renewal will be updated to $9.99/year.'}
            </p>
          )}

          {isUpgrading && selectedTier === SubscriptionTier.LIFETIME && (
            <div style={{ fontSize: 'small', color: '#666', marginTop: '10px' }}>
              <p style={{ marginBottom: '8px' }}>
                <strong>Upgrade to lifetime access!</strong> This is a one-time payment - no renewals, ever.
              </p>
              {lifetimeDiscount > 0 && (
                <p style={{ color: '#4285f4', fontWeight: 'bold', marginTop: '8px' }}>
                  💰 You'll receive a ${lifetimeDiscount.toFixed(2)} discount for your remaining subscription time!
                  <br />
                  <span style={{ fontSize: 'x-small', fontWeight: 'normal', color: '#888' }}>
                    Applied automatically at checkout
                  </span>
                </p>
              )}
            </div>
          )}

          {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
          {success && <p style={{ color: 'green', marginTop: '10px' }}>{success}</p>}

          <button
            style={buttonStyle}
            onClick={handleSubscribe}
            disabled={!selectedTier || loading || !!success}
          >
            {loading ? 'Processing...' : success ? (isUpgrading ? 'Upgraded!' : 'Subscribed!') : (isUpgrading ? 'Upgrade Now' : 'Subscribe Now')}
          </button>

          <p style={{ fontSize: 'x-small', marginTop: '20px' }}>
            Payment securely processed by Stripe.
            {selectedTier === SubscriptionTier.LIFETIME ? (
              ' Lifetime access - one-time payment, never expires!'
            ) : (
              ' Your subscription will automatically renew annually. You can cancel anytime from your account settings.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;